import React from 'react';
import PropTypes from 'prop-types';
import { 
  Download,
  ExternalLink,
  Link as LinkIcon
} from 'lucide-react'; 
// Importamos solo los iconos necesarios: Download, ExternalLink, LinkIcon

// Asegúrate de que estos archivos estén disponibles en tu proyecto
// import './AboutSection.css';
// import Gallery from '../sections/Gallery'; // <-- La versión real debe estar aquí

// Componente de Galería simulado. Si se muestra esto, el componente real 'Gallery' no está disponible.
const Gallery = ({ imagesProp, itemsPerPage }) => (
    // Estilo de placeholder mejorado para dual-theme
    <div className="bg-gray-100 dark:bg-gray-800 p-8 rounded-2xl shadow-xl flex justify-center items-center h-72 border border-gray-300 dark:border-gray-700 transition duration-500">
      <p className="text-gray-600 dark:text-gray-400 italic text-center text-lg">
        [Componente Gallery no disponible] <br/> Mostrando {imagesProp.length} escudos municipales.
      </p>
    </div>
);


// Definición de la lista de imágenes (simulada o real, dependiendo del entorno)
const imageList = [
    {
        id: '1',
        src: 'images/cabo-corrientes-escudo.png',
        thumbnail: 'images/cabo-corrientes-escudo.png',
        alt: 'Escudo de Cabo Corrientes',
        caption: 'Cabo Corrientes.'
    },
    {
        id: '2',
        src: 'images/tomatlan-escudo.png',
        thumbnail: 'images/tomatlan-escudo.png',
        alt: 'Escudo de Tomatlán',
        caption: 'Tomatlán.'
    },
    {
        id: '3',
        src: 'images/la_huerta-escudo.png',
        thumbnail: 'images/la_huerta-escudo.png',
        alt: 'Escudo de La Huerta',
        caption: 'La Huerta.'
    },
    {
        id: '4',
        src: 'images/cihuatlan-escudo.png',
        thumbnail: 'images/cihuatlan-escudo.png',
        alt: 'Escudo de Cihuatlán',
        caption: 'Cihuatlán.'
    },
];

// Estilos de los botones principales (USANDO VERDE VIBRANTE Y SOPORTE DARK MODE)
const LinkButtonClasses = "inline-flex items-center bg-green-600 text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:bg-green-700 transition duration-300 transform hover:scale-[1.02] mr-4 dark:bg-green-700 dark:hover:bg-green-600";

// Componente Principal
const AboutSection = ({ title, subtitle, content, image, links, dates, phase, mainDocumentLink, mainDocumentLinkR, secondaryDocumentLink }) => {
  
  return (
    // Fondo más contrastante con soporte dark mode
    <section id="about" className="about-section py-20 md:py-32 bg-white dark:bg-gray-900 transition duration-500 environmental-theme"> 
      <div className="container mx-auto px-4">
        
        {/* Título de la Sección */}
        <div className="text-center mb-16">
          {/* Título vibrante (verde) */}
          <h2 id="about-title" className="text-4xl md:text-6xl font-extrabold text-green-800 dark:text-green-400 mb-4 leading-tight transition duration-500">{title}</h2>
          {/* Subtítulo suave */}
          {subtitle && <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto transition duration-500">{subtitle}</p>}
        </div>
        
        {/* Contenido principal en dos columnas */}
        <div className="grid md:grid-cols-2 gap-16 items-start">
          
          {/* Columna de Contenido y Enlaces */}
          {/* Añadimos un contenedor de tarjeta para destacar el contenido */}
          <div className={`p-8 md:p-10 bg-gray-100 dark:bg-gray-800 rounded-3xl shadow-2xl transition duration-500 ${!image ? 'md:col-span-2' : ''}`}>
            
            {phase && (
              // Fase con estilo destacado (verde)
              <p className="text-xl font-bold text-green-700 mb-6 bg-green-100 dark:bg-green-900 py-2 px-4 rounded-full inline-block shadow-md dark:text-green-300">
                Fase Actual: <span className="text-gray-800 dark:text-gray-200 font-extrabold ml-1">{phase}</span>
              </p>
            )}
            
            {/* Contenido HTML */}
            <div className="text-gray-700 dark:text-gray-300 space-y-6 leading-relaxed text-lg transition duration-500" dangerouslySetInnerHTML={{ __html: content }}></div>
            
            {/* Contenedor de botones principales */}
            <div className="mt-10 flex flex-wrap gap-4 items-center">
                {mainDocumentLink && (
                  <a 
                    href={mainDocumentLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={LinkButtonClasses}
                  >
                    <Download size={20} className="mr-3" /> {mainDocumentLink.text}
                  </a>
                )}
                
                {mainDocumentLinkR && (
                  <a 
                    href={mainDocumentLinkR.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={LinkButtonClasses}
                  >
                    <Download size={20} className="mr-3" /> {mainDocumentLinkR.text}
                  </a>
                )}

                {secondaryDocumentLink && (
                  <a 
                    href={secondaryDocumentLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    // Estilo secundario más visible y con dark mode (verde)
                    className="inline-flex items-center text-green-600 border-2 border-green-600 font-semibold py-3 px-8 rounded-full hover:bg-green-50 transition duration-300 dark:text-green-400 dark:border-green-400 dark:hover:bg-gray-700"
                  >
                    <ExternalLink size={20} className="mr-3" /> {secondaryDocumentLink.text}
                  </a>
                )}
            </div>
            
            {/* Enlaces secundarios (links prop) */}
            {links && links.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-300 dark:border-gray-700 space-y-4">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200">Documentos Adicionales:</h4>
                {links.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    // Estilo de enlace animado (verde)
                    className="flex items-center text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium transition duration-200 group"
                  >
                    <LinkIcon size={18} className="mr-3 flex-shrink-0 group-hover:rotate-6 transition duration-200" /> 
                    {link.text} 
                    <ExternalLink size={14} className="ml-2 opacity-70 group-hover:opacity-100 transition duration-200"/>
                  </a>
                ))}
              </div>
            )}

            {/* Fechas Importantes */}
            {dates && dates.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-300 dark:border-gray-700">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Fechas Importantes:</h4>
                <ul className="about-section-list text-gray-700 dark:text-gray-300 space-y-3">
                  {dates.map((dateItem, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2 flex-shrink-0 text-xl leading-none">&raquo;</span> {/* Icono más dinámico (verde) */}
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{dateItem.label}:</span> 
                      <span className="ml-1">{dateItem.date}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Columna de Imagen/Galería */}
          {image && (
            <div className="animate__animated animate__fadeInLeft">
              {/* Se utiliza el componente Gallery y la lista de escudos */}
              <Gallery imagesProp={imageList} itemsPerPage={4} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// PropTypes para documentar todos los props (sin cambios, solo se incluye para el componente completo)
AboutSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  content: PropTypes.string.isRequired,
  image: PropTypes.string, 
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    })
  ),
  phase: PropTypes.string,
  mainDocumentLink: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  mainDocumentLinkR: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  secondaryDocumentLink: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
};

export default AboutSection;